import { useEffect, useState } from "react";
import logo from "../assets/TUDOFARMA_CURVAS_BCO_PDV.svg";
import { InvestmentModal } from "@/components/InvestmentModal";
import {
  InvestmentDetailModal,
  type SavedInvestmentDetail,
} from "@/components/InvestmentDetailModal";
import { NegotiationsModal } from "@/components/NegotiationsModal";
import { PdvInvestmentsModal } from "@/components/PdvInvestmentsModal";
import { PesquisasModal } from "@/components/PesquisasModal";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { X, ChevronRight } from "lucide-react";

export default function Index() {
  const [modalOpen, setModalOpen] = useState(false);
  const [investments, setInvestments] = useState<SavedInvestmentDetail[]>([]);
  const [listOpen, setListOpen] = useState(false);
  const [pdvOpen, setPdvOpen] = useState(false);
  const [negotiationsOpen, setNegotiationsOpen] = useState(false);
  const [pesquisasOpen, setPesquisasOpen] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);

  const selected = selectedIdx !== null ? investments[selectedIdx] : null;

  useEffect(() => {
    document.title = "TudoFarma";
  }, []);

  return (
    <main className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden bg-gradient-to-br from-tudo-blue via-tudo-blue to-[oklch(0.28_0.12_273.67)] px-4 py-8 sm:px-6 lg:px-8">
      {/* Abstract background elements */}
      <div className="absolute -top-[10%] -left-[10%] h-[500px] w-[500px] rounded-full bg-white/10 blur-3xl" />
      <div className="absolute -bottom-[15%] -right-[10%] h-[600px] w-[600px] rounded-full bg-tudo-green/20 blur-3xl" />
      <div className="absolute top-1/3 left-1/4 h-72 w-72 rounded-full bg-tudo-blue/40 blur-3xl" />
      {/* Geometric accents */}
      <div className="absolute top-1/4 right-10 h-24 w-24 rounded-full border-4 border-white/15" />
      <div className="absolute bottom-1/4 left-10 h-32 w-32 rotate-12 rounded-2xl border-4 border-white/10" />
      {/* Subtle grid overlay */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      <div className="relative z-10 flex w-full max-w-md flex-col items-center">
        <img
          src={logo}
          alt="TudoFarma Logo"
          className="h-20 w-auto drop-shadow-lg sm:h-24 md:h-28"
        />
        <div className="mt-10 w-full space-y-3 sm:mt-12 sm:space-y-4">
          <button
            onClick={() => setModalOpen(true)}
            className="group flex w-full items-center justify-between rounded-2xl border border-border bg-card px-5 py-4 shadow-sm transition-all hover:border-tudo-green hover:shadow-md focus:outline-none focus:ring-2 focus:ring-tudo-green focus:ring-offset-2 sm:px-6"
          >
            <span className="text-base font-semibold text-foreground group-hover:text-tudo-green sm:text-lg">
              Solicitar novo investimento
            </span>
            <div className="rounded-lg bg-muted p-2 transition-colors group-hover:bg-tudo-green/10">
              <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-tudo-green" />
            </div>
          </button>
          {investments.length > 0 && (
            <button
              onClick={() => setListOpen(true)}
              className="group flex w-full items-center justify-between rounded-2xl border border-border bg-card px-5 py-4 shadow-sm transition-all hover:border-tudo-blue hover:shadow-md sm:px-6"
            >
              <span className="text-base font-semibold text-foreground group-hover:text-tudo-blue sm:text-lg">
                {investments.length === 1
                  ? "Investimento solicitado (1)"
                  : `Investimentos solicitados (${investments.length})`}
              </span>
              <div className="rounded-lg bg-muted p-2 transition-colors group-hover:bg-tudo-blue/10">
                <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-tudo-blue" />
              </div>
            </button>
          )}
          <button
            onClick={() => setPdvOpen(true)}
            className="group flex w-full items-center justify-between rounded-2xl border border-border bg-card px-5 py-4 shadow-sm transition-all hover:border-tudo-blue hover:shadow-md focus:outline-none focus:ring-2 focus:ring-tudo-blue focus:ring-offset-2 sm:px-6"
          >
            <span className="text-base font-semibold text-foreground group-hover:text-tudo-blue sm:text-lg">
              Investimento no PDV
            </span>
            <div className="rounded-lg bg-muted p-2 transition-colors group-hover:bg-tudo-blue/10">
              <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-tudo-blue" />
            </div>
          </button>
          <button
            onClick={() => setNegotiationsOpen(true)}
            className="group flex w-full items-center justify-between rounded-2xl border border-border bg-card px-5 py-4 shadow-sm transition-all hover:border-tudo-green hover:shadow-md focus:outline-none focus:ring-2 focus:ring-tudo-green focus:ring-offset-2 sm:px-6"
          >
            <span className="text-base font-semibold text-foreground group-hover:text-tudo-green sm:text-lg">
              Negociações
            </span>
            <div className="rounded-lg bg-muted p-2 transition-colors group-hover:bg-tudo-green/10">
              <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-tudo-green" />
            </div>
          </button>
          <button
            onClick={() => setPesquisasOpen(true)}
            className="group flex w-full items-center justify-between rounded-2xl border border-border bg-card px-5 py-4 shadow-sm transition-all hover:border-tudo-blue hover:shadow-md focus:outline-none focus:ring-2 focus:ring-tudo-blue focus:ring-offset-2 sm:px-6"
          >
            <span className="text-base font-semibold text-foreground group-hover:text-tudo-blue sm:text-lg">
              Pesquisas
            </span>
            <div className="rounded-lg bg-muted p-2 transition-colors group-hover:bg-tudo-blue/10">
              <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-tudo-blue" />
            </div>
          </button>
        </div>
      </div>
      <InvestmentModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        onSave={(data) => setInvestments((prev) => [...prev, data])}
      />

      <Dialog open={listOpen} onOpenChange={setListOpen}>
        <DialogContent className="w-full max-w-lg overflow-hidden rounded-xl border-none p-0 shadow-2xl [&>button]:hidden">
          <div className="relative flex items-center justify-between bg-tudo-blue px-5 py-3">
            <DialogTitle className="text-left text-sm font-bold uppercase tracking-wide text-white">
              Investimentos solicitados
            </DialogTitle>
            <button
              onClick={() => setListOpen(false)}
              className="text-white/80 transition-colors hover:text-white"
              aria-label="Fechar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex max-h-[60vh] flex-col gap-2 overflow-y-auto p-5">
            {investments.map((inv, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => {
                  setSelectedIdx(idx);
                  setListOpen(false);
                }}
                className="w-full rounded-md border border-gray-300 bg-white px-4 py-3 text-left text-sm transition-colors hover:border-tudo-blue hover:bg-tudo-blue/5"
              >
                <div className="font-semibold text-foreground">
                  {inv.tipo || "Investimento"} — {inv.unidadeNegocios}
                </div>
                <div className="text-xs text-gray-500">
                  {inv.cnpjMatriz}
                  {inv.natureza && ` • ${inv.natureza}${inv.naturezaValor ? `: ${inv.naturezaValor}` : ""}`}
                  {inv.demaisContrapartida && ` • ${inv.demaisContrapartida.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}`}
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <PdvInvestmentsModal open={pdvOpen} onOpenChange={setPdvOpen} />

      <NegotiationsModal
        open={negotiationsOpen}
        onOpenChange={setNegotiationsOpen}
        onSolicitarInvestimento={() => setModalOpen(true)}
      />

      <PesquisasModal open={pesquisasOpen} onOpenChange={setPesquisasOpen} />

      {selected && (
        <InvestmentDetailModal
          open={selectedIdx !== null}
          onOpenChange={(o) => {
            if (!o) setSelectedIdx(null);
          }}
          cnpjMatriz={selected.cnpjMatriz}
          unidadeNegocios={selected.unidadeNegocios}
          tipo={selected.tipo}
          subTipo={selected.subTipo}
          natureza={selected.natureza}
          naturezaValor={selected.naturezaValor}
          vigenciaDe={selected.vigenciaDe}
          vigenciaAte={selected.vigenciaAte}
          demandaAcordada={selected.demandaAcordada}
          prazo={selected.prazo}
          demaisContrapartida={selected.demaisContrapartida}
          onSave={(data) => {
            if (selectedIdx !== null) {
              setInvestments((prev) => prev.map((p, i) => (i === selectedIdx ? data : p)));
              setSelectedIdx(null);
            }
          }}
        />
      )}
    </main>
  );
}
